/**
 * TeamPage - Main team management page with Members / Hierarchy / Invitations tabs
 */

import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
// `Plus` is used by the temporarily-hidden Invite button — re-add when re-enabling.
import { Users, GitBranch, Mail, Upload, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/Button';
import { MembersTab } from './MembersTab';
import { HierarchyTab } from './HierarchyTab';
import { InvitationsTab } from './InvitationsTab';
import { TeamMemberDetailDrawer } from './TeamMemberDetailDrawer';
import { InviteModal } from './InviteModal';
import { BulkInviteModal } from './BulkInviteModal';
import { AddUserModal } from './AddUserModal';
import { BulkUserUploadModal } from './BulkUserUploadModal';
import invitationService from '../../services/api/invitationService';
import type { Member } from '../../services/api/userService';
import { useAuthStore } from '../../stores/authStore';

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

// Used by the temporarily-hidden Invite button (see HeaderActions). Kept for re-enable.
// const InviteBtn = styled(Button)`
//   /* uses primary-blue variant — dark teal with accent stripe */
// `;

const TabsBar = styled.div`
  display: flex;
  align-items: flex-end;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borders.light};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  gap: 0;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary.base : theme.colors.text.secondary};
  border-bottom: 2px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary.base : 'transparent')};
  margin-bottom: -2px;
  background: transparent;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.base};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 ${({ theme }) => theme.spacing[1]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.status.info.bg};
  color: ${({ theme }) => theme.colors.status.info.text};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  border: 1px solid ${({ theme }) => theme.colors.status.info.border};
`;

const TabContent = styled.div`
  flex: 1;
`;

// ============================================================================
// Component
// ============================================================================

type TabId = 'members' | 'hierarchy' | 'invitations';

export const TeamPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('members');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isBulkInviteOpen, setIsBulkInviteOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isBulkUserUploadOpen, setIsBulkUserUploadOpen] = useState(false);
  const { hasPermission } = useAuthStore();

  const handleCloseMemberDrawer = useCallback(() => setSelectedMember(null), []);

  // Fetch invitation count for badge
  const { data: invitationsData } = useQuery({
    queryKey: ['invitations'],
    queryFn: () => invitationService.getInvitations(),
    // Gracefully handle 404 / not-yet-implemented API
    retry: false,
  });

  const pendingCount = invitationsData?.pending?.length ?? 0;

  const tabs: { id: TabId; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'members', label: 'Members', icon: <Users /> },
    { id: 'hierarchy', label: 'Hierarchy', icon: <GitBranch /> },
    { id: 'invitations', label: 'Invitations', icon: <Mail />, badge: pendingCount },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Team</PageTitle>
        {hasPermission('team:manage:actions') && (
          <HeaderActions>
            {/* Invite / Bulk Invite temporarily hidden — kept for future re-enable.
            <Button
              variant="secondary"
              leftIcon={<Upload />}
              onClick={() => setIsBulkInviteOpen(true)}
            >
              Bulk Invite
            </Button>
            <InviteBtn
              variant="primary-blue"
              leftIcon={<Plus />}
              onClick={() => setIsInviteOpen(true)}
            >
              Invite
            </InviteBtn>
            */}
            <Button
              variant="secondary"
              leftIcon={<Upload />}
              onClick={() => setIsBulkUserUploadOpen(true)}
            >
              {t('team.buttonBulkUpload')}
            </Button>
            <Button
              variant="primary"
              leftIcon={<UserPlus />}
              onClick={() => setIsAddUserOpen(true)}
            >
              {t('team.buttonAddUser')}
            </Button>
          </HeaderActions>
        )}
      </PageHeader>

      <TabsBar>
        {tabs.map((tab) => (
          <Tab key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
            {tab.icon}
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && <TabBadge>{tab.badge}</TabBadge>}
          </Tab>
        ))}
      </TabsBar>

      <TabContent>
        {activeTab === 'members' && (
          <MembersTab onMemberClick={(member) => setSelectedMember(member)} />
        )}
        {activeTab === 'hierarchy' && <HierarchyTab />}
        {activeTab === 'invitations' && <InvitationsTab />}
      </TabContent>

      {/* Right slide-in drawer for member details — mounted only while open so its
          body-scroll lock and key handlers don't run on the whole Team page. */}
      {selectedMember && (
        <TeamMemberDetailDrawer member={selectedMember} onClose={handleCloseMemberDrawer} />
      )}

      {/* Invite modal */}
      <InviteModal isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />
      <BulkInviteModal isOpen={isBulkInviteOpen} onClose={() => setIsBulkInviteOpen(false)} />
      <AddUserModal isOpen={isAddUserOpen} onClose={() => setIsAddUserOpen(false)} />
      <BulkUserUploadModal
        isOpen={isBulkUserUploadOpen}
        onClose={() => setIsBulkUserUploadOpen(false)}
      />
    </PageContainer>
  );
};

export default TeamPage;
